import config from '@/app/config'

/**
 * Die drei Rollen der App und alles, was ihre Anmeldung unterscheidet. SignInPage,
 * SignUpPage, AuthGate und SetNewPassword lesen hier, statt je Rolle eine eigene
 * Seite zu pflegen.
 */
export type AppRole = 'user' | 'me' | 'midwife'

/** Anmeldung ueber Fremdanbieter. */
export type OAuthProvider = 'google' | 'apple'

export type RoleConfig = {
  role: AppRole
  /** Startseite nach erfolgreicher Anmeldung. */
  homePath: string
  signInPath: string
  signUpPath: string
  /** Ziel des Zurück-Pfeils auf den Anmeldeseiten. */
  backPath: string
  signInHeading: string
  signUpHeading: string
  /** Rollen, die Nhost dem Konto erlaubt; `role` ist immer darunter. */
  allowedRoles: string[]
  /**
   * Gibt es für diese Rolle eine Registrierung? Steuert den Link auf der Anmeldeseite
   * und den Abbruch in SignUpPage - ohne den wäre `signUpPath` auch bei abgeschalteter
   * Registrierung erreichbar, geschützt allein durch `registrationCode` aus dem Bundle.
   */
  signUpEnabled: boolean
  showForgotPassword: boolean
  /**
   * Welche Fremdanbieter die Rolle anbietet, in der Reihenfolge der Knöpfe. Leer
   * heißt: nur E-Mail und Passwort. Apple fehlt überall, weil es ein bezahltes
   * Apple-Developer-Konto braucht und bei Nhost nicht freigeschaltet ist - der Knopf
   * liefe sonst in "Diese Anmeldeart ist noch nicht freigeschaltet".
   */
  oauthProviders: OAuthProvider[]
  /** Muss bei der Registrierung eingegeben werden, sonst gibt es kein Konto. */
  registrationCode?: string
  /** Zusätzliche Metadaten, die bei der Registrierung ans Nutzerkonto gehängt werden. */
  signUpMetadata?: Record<string, unknown>
}

export const ROLE_CONFIG: Record<AppRole, RoleConfig> = {
  user: {
    role: 'user',
    homePath: '/user',
    signInPath: '/sign-in/email-password',
    signUpPath: '/sign-up/email-password',
    backPath: '/',
    signInHeading: 'Anmelden',
    signUpHeading: 'Registrieren',
    allowedRoles: ['user'],
    signUpEnabled: true,
    showForgotPassword: true,
    oauthProviders: ['google'],
  },
  me: {
    role: 'me',
    homePath: '/me',
    signInPath: '/sign-in-me',
    signUpPath: '/sign-up-me',
    backPath: '/',
    signInHeading: 'Me Anmeldung',
    signUpHeading: 'Me Registrierung',
    allowedRoles: ['user', 'me'],
    signUpEnabled: config.showMeSignUp,
    showForgotPassword: false,
    oauthProviders: [],
    registrationCode: 'fucknestle',
  },
  midwife: {
    role: 'midwife',
    homePath: '/midwife',
    signInPath: '/hebamme/sign-in',
    signUpPath: '/hebamme/sign-up',
    backPath: '/partner',
    signInHeading: 'Als Hebamme Anmelden',
    signUpHeading: 'Als Hebamme Registrieren',
    allowedRoles: ['user', 'midwife'],
    signUpEnabled: true,
    showForgotPassword: true,
    oauthProviders: ['google'],
    signUpMetadata: { isHebamme: true },
  },
}

/** Startseite für eine Rolle aus dem JWT; unbekannte Rollen landen bei den Nutzerinnen. */
export function homePathForRole(role: string | null | undefined): string {
  return role && role in ROLE_CONFIG
    ? ROLE_CONFIG[role as AppRole].homePath
    : ROLE_CONFIG.user.homePath
}
